import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode
} from 'react';

const DURATION_MS = 220;
const EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';

/** Keep visited fields mounted so folding never discards an in-progress edit. */
export default function CreatorQuoteCollapse({
  id,
  open,
  overflowVisible = false,
  children
}: {
  id: string;
  open: boolean;
  overflowVisible?: boolean;
  children: ReactNode;
}) {
  const [hasOpened, setHasOpened] = useState(open);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<Animation | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (open) setHasOpened(true);
  }, [open]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const preference = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    let active = true;
    let targetHeight = 0;
    const animate = () => {
      // Read before cancelling, so a quick reversal starts at its visible height.
      const height = container.getBoundingClientRect().height;
      const opacity = window.getComputedStyle(container).opacity;
      animationRef.current?.cancel();
      animationRef.current = null;
      targetHeight = open ? content.getBoundingClientRect().height : 0;
      // Auto at rest also accommodates late price/error content and H5 resizing.
      container.style.height = open ? 'auto' : '0px';
      container.style.opacity = open ? '1' : '0';
      if (
        !initialized.current ||
        preference?.matches ||
        !container.animate ||
        (height === targetHeight && Number(opacity) === Number(open))
      )
        return;

      const animation = container.animate(
        [
          { height: `${height}px`, opacity },
          { height: `${targetHeight}px`, opacity: open ? '1' : '0' }
        ],
        { duration: DURATION_MS, easing: EASING }
      );
      animationRef.current = animation;
      animation.onfinish = () => {
        if (animationRef.current === animation) animationRef.current = null;
      };
    };
    animate();
    initialized.current = true;

    const observer =
      open && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            if (
              active &&
              animationRef.current?.playState === 'running' &&
              content.getBoundingClientRect().height !== targetHeight
            )
              animate();
          })
        : null;
    observer?.observe(content);
    const reduceMotion = () => {
      if (preference?.matches) {
        animationRef.current?.cancel();
        animationRef.current = null;
      }
    };
    preference?.addEventListener?.('change', reduceMotion);
    return () => {
      active = false;
      observer?.disconnect();
      preference?.removeEventListener?.('change', reduceMotion);
    };
  }, [open]);

  useLayoutEffect(() => () => animationRef.current?.cancel(), []);

  return (
    <div
      ref={containerRef}
      id={id}
      data-testid="placement-collapse"
      aria-hidden={!open || undefined}
      {...(!open ? { inert: '' } : {})}
      className={
        open && overflowVisible ? 'overflow-visible' : 'overflow-hidden'
      }
    >
      <div ref={contentRef} className="flow-root">
        {open || hasOpened ? children : null}
      </div>
    </div>
  );
}
